import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaEnvelope, FaLock, FaUser } from 'react-icons/fa';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import { CUSTOMER_PORTAL_URL } from '../config';

// E1: previously called the legacy website-only backend directly
// (services/api.js's authAPI, since removed) and stored the resulting
// JWT in localStorage, then force-reloaded the page. Now goes through
// the canonical Sheba auth store (src/store/authStore.js) — the same
// cookie-session-issuing backend/auth_routes.py::register()/login()
// endpoints the mobile app and customer portal already use, so an
// account created here works immediately in the mobile app with the
// same credentials (and vice versa). The legacy "phone" field is
// dropped — backend/models.py::UserRegister doesn't accept one.
//
// E2.2: the post-auth redirect now goes to CUSTOMER_PORTAL_URL (a full
// page navigation via window.location, not React Router's navigate —
// frontend/customer/ is a separate application/build, so a client-side
// route change can't reach it) instead of this site's own, now-
// superseded /dashboard. See src/config.js for why the default is safe.
const AuthModal = ({ isOpen, onClose, mode = 'login', onSwitchMode }) => {
  const { t } = useTranslation();
  const register = useAuthStore((s) => s.register);
  const login = useAuthStore((s) => s.login);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
      } else {
        await register(formData.email, formData.password, formData.name);
      }

      setSuccess(mode === 'login' ? t('auth.loginButton') + ' successful!' : t('auth.registerButton') + ' successful!');
      setTimeout(() => {
        onClose();
        window.location.href = CUSTOMER_PORTAL_URL;
      }, 800);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong rounded-3xl p-8 w-full max-w-md relative"
          data-testid="auth-modal"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            data-testid="close-modal-btn"
          >
            <FaTimes size={24} />
          </button>

          {/* Title */}
          <h2 className="text-3xl font-bold text-white mb-2 text-center">
            {mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount')}
          </h2>
          <p className="text-white/60 text-center mb-6">
            {mode === 'login'
              ? t('auth.loginSubtitle')
              : t('auth.registerSubtitle')}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div className="relative">
                <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  name="name"
                  placeholder={t('auth.fullName')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  minLength={2}
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                  data-testid="fullname-input"
                />
              </div>
            )}

            <div className="relative">
              <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="email"
                name="email"
                placeholder={t('auth.emailAddress')}
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                data-testid="email-input"
              />
            </div>

            <div className="relative">
              <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="password"
                name="password"
                placeholder={t('auth.password')}
                value={formData.password}
                onChange={handleChange}
                required
                minLength={mode === 'register' ? 12 : undefined}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                data-testid="password-input"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-3 text-green-400 text-sm" data-testid="success-message">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary !py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {loading
                ? t('auth.pleaseWait')
                : mode === 'login'
                ? t('auth.loginButton')
                : t('auth.registerButton')}
            </button>
          </form>

          {/* Switch Mode */}
          <div className="mt-6 text-center">
            <p className="text-white/60">
              {mode === 'login'
                ? t('auth.dontHaveAccount') + ' '
                : t('auth.alreadyHaveAccount') + ' '}
              <button
                onClick={onSwitchMode}
                className="text-[#00d4ff] hover:underline font-semibold"
                data-testid="switch-mode-btn"
              >
                {mode === 'login' ? t('auth.switchToRegister') : t('auth.switchToLogin')}
              </button>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;